import React from 'react';
import { QuickAnalyzeResponse } from '../types';

interface QuickAnalyzeResultsOverlayProps {
  analysisResult: QuickAnalyzeResponse | null;
  isLoading: boolean;
  onApply: () => void;
  onCancel: () => void;
}

const QuickAnalyzeResultsOverlay: React.FC<QuickAnalyzeResultsOverlayProps> = ({
  analysisResult,
  isLoading,
  onApply,
  onCancel,
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100, // Above SlideEditModal (1050)
        padding: '20px',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '800px',
          maxHeight: '90vh',
          width: '100%',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px',
            }}
          >
            Quick Analyze Results
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            {isLoading ? 'Analyzing...' : analysisResult ? `AI-powered assessment • ${analysisResult.imageCount} images analyzed` : 'No results available'}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner-lg" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Analyzing slide images...</p>
          </div>
        )}

        {/* Results Display */}
        {!isLoading && analysisResult && (
          <div>
            {/* Display user's input text if provided */}
            {analysisResult.userProvidedText && (
              <div
                style={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: 'var(--bg-primary)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--primary-light)',
                }}
              >
                <h4
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    color: 'var(--text-primary)',
                  }}
                >
                  Your Input:
                </h4>
                <p
                  style={{
                    fontSize: '14px',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    color: 'var(--text-primary)',
                  }}
                >
                  {analysisResult.userProvidedText}
                </p>
              </div>
            )}

            {/* Quick Assessment */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '12px',
                }}
              >
                🎯 Quick Assessment
              </h3>
              {analysisResult.suggestedTitle && (
                <p style={{ marginBottom: '8px' }}>
                  <strong>Title:</strong> {analysisResult.suggestedTitle}
                </p>
              )}
              {analysisResult.suggestedEra && (
                <p style={{ marginBottom: '8px' }}>
                  <strong>Era:</strong> {analysisResult.suggestedEra}
                </p>
              )}
              {analysisResult.suggestedMaker && (
                <p style={{ marginBottom: '8px' }}>
                  <strong>Maker:</strong> {analysisResult.suggestedMaker}
                </p>
              )}
              {analysisResult.stylePeriod && (
                <p style={{ marginBottom: '8px' }}>
                  <strong>Style:</strong> {analysisResult.stylePeriod}
                </p>
              )}
              {analysisResult.materials && analysisResult.materials.length > 0 && (
                <p style={{ marginBottom: '8px' }}>
                  <strong>Materials:</strong> {analysisResult.materials.join(', ')}
                </p>
              )}
            </div>

            {/* Price Tag Detection */}
            {analysisResult.priceTagDetected && analysisResult.detectedPrice && (
              <div
                style={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: 'var(--bg-accent)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--success)',
                }}
              >
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  💰 Price Tag Detected
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p
                    style={{
                      marginBottom: '4px',
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: 'var(--success-dark)',
                    }}
                  >
                    {analysisResult.detectedCurrency || 'USD'} ${analysisResult.detectedPrice.toFixed(2)}
                  </p>
                  {analysisResult.detectedRetailer && (
                    <p style={{ marginBottom: '4px', color: 'var(--text-primary)' }}>
                      <strong>Store:</strong> {analysisResult.detectedRetailer}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Distinguishing Features */}
            {analysisResult.distinguishingFeatures && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  ✨ Distinguishing Features
                </h3>
                <p style={{ color: 'var(--text-primary)' }}>{analysisResult.distinguishingFeatures}</p>
              </div>
            )}

            {/* Condition Notes */}
            {analysisResult.conditionNotes && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  🔍 Condition
                </h3>
                <p style={{ color: 'var(--text-primary)' }}>{analysisResult.conditionNotes}</p>
              </div>
            )}

            {/* Collectibility Notes */}
            {analysisResult.collectibilityNotes && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  💎 Collectibility
                </h3>
                <p style={{ color: 'var(--text-primary)' }}>{analysisResult.collectibilityNotes}</p>
              </div>
            )}

            {/* Marketplace Keywords */}
            {analysisResult.marketplaceKeywords && analysisResult.marketplaceKeywords.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  🏷️ Marketplace Keywords
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {analysisResult.marketplaceKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: 'var(--border-light)',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--border-light)',
          }}
        >
          <button
            onClick={onApply}
            disabled={isLoading || !analysisResult}
            className="btn btn-primary"
            style={{
              flex: 1,
              padding: '12px 24px',
              opacity: isLoading || !analysisResult ? 0.5 : 1,
              cursor: isLoading || !analysisResult ? 'not-allowed' : 'pointer',
            }}
          >
            Apply to Slide
          </button>
          <button
            onClick={onCancel}
            className="btn btn-ghost"
            style={{
              flex: 1,
              padding: '12px 24px',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAnalyzeResultsOverlay;
